// Central authenticated API client.
// Replaces the per-page `import.meta.env.VITE_API_URL` + localStorage token +
// fetch boilerplate. All calls attach the bearer token, prefix the base URL,
// parse JSON, and throw ApiError on a failed response or `{ success: false }`.

const BASE = import.meta.env.VITE_API_URL || '';

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(message: string, status: number, body?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = localStorage.getItem('token');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(extra || {}),
  };
}

async function request<T = any>(method: string, path: string, body?: unknown): Promise<T> {
  const hasBody = body !== undefined;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: hasBody ? authHeaders({ 'Content-Type': 'application/json' }) : authHeaders(),
    body: hasBody ? JSON.stringify(body) : undefined,
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    throw new ApiError(json?.error || json?.message || res.statusText, res.status, json);
  }
  return json as T;
}

export const api = {
  get: <T = any>(path: string) => request<T>('GET', path),
  post: <T = any>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T = any>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T = any>(path: string) => request<T>('DELETE', path),
  // Escape hatch for non-JSON / streaming responses; still attaches auth + base URL.
  raw: (path: string, init?: RequestInit) =>
    fetch(`${BASE}${path}`, { ...init, headers: { ...authHeaders(), ...(init?.headers as any) } }),
};

// Convenience: GET an endpoint shaped `{ success, data }` and return `data`.
export async function getData<T = any>(path: string, fallback?: T): Promise<T> {
  const json = await api.get<{ data?: T }>(path);
  return (json?.data ?? fallback) as T;
}
